import { Button, Switch } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import { organizationListContext } from 'BrokerTsLib/src/context'
import type {
  OrganizationUnitGridItem,
  OrganizationUnitPagedQuery,
} from 'BrokerTsLib/src/dtos'
import type { FlexGroupOrganization } from 'BrokerTsLib/src/dtos/FlexGroup/FlexGroupOrganization'
import { noop } from 'lodash'
import { useState } from 'react'

import { FormSection, FormSectionBody, FormSectionDescription, FormSectionHeader, FormSectionTitle } from '@/components/FormSection'
import { Trash } from '@/icons'

import {
  OrganizationListCityColumn,
  OrganizationListDescriptionColumn,
  OrganizationListNameColumn,
  OrganizationListTypeColumn,
  OrganizationListValidColumn,
  OrganizationsList,
} from '../organization/OrganizationList'
import { OrganizationListFilter } from '../organization/OrganizationListFilter'
import { AddFlexGroupOrganization } from './AddFlexGroupOrganization'

interface FlexGroupOrganizationListSectionProps {
  title: string
  description: string
  flexGroupOrganizations: FlexGroupOrganization[]
  isLoading: boolean
  addOrganizationAsync: (organizationUnitId: number, includeSubordinate: boolean) => Promise<any>
  updateOrganizationAsync?: (organizationUnitId: number, includeSubordinate: boolean) => Promise<any>
  removeOrganizationAsync: (organizationUnitId: number) => Promise<any>
}

export const FlexGroupOrganizationListSection = (
  {
    title,
    description,
    flexGroupOrganizations,
    isLoading,
    addOrganizationAsync,
    updateOrganizationAsync,
    removeOrganizationAsync,
    ...rest
  }: FlexGroupOrganizationListSectionProps) => {
  const [filter, setFilter] = useState<OrganizationUnitPagedQuery>({
    showInvalid: false,
    showRootOnly: true,
    pageIndex: 1,
  })

  const organizationsFilter = (item: OrganizationUnitGridItem) => {
    if (!item.valid && !filter.showInvalid) return false
    if (
      filter.name &&
      !(
        item.name?.toLowerCase().includes(filter.name.toLowerCase()) ||
        item.contactName?.toLowerCase().includes(filter.name.toLowerCase()) ||
        item.description?.toLowerCase().includes(filter.name.toLowerCase()) ||
        item.addressCity?.toLowerCase().includes(filter.name.toLowerCase())
      )
    )
      return false

    return true
  }

  const organizations = flexGroupOrganizations.map(x => x.organizationUnit)

  const filteredItems = organizations.filter(organizationsFilter)

  const columnsDefinitions = [
    OrganizationListNameColumn,
    OrganizationListCityColumn,
    OrganizationListDescriptionColumn,
    OrganizationListValidColumn,
    OrganizationListTypeColumn,
  ]

  if (updateOrganizationAsync) {
    columnsDefinitions.push({
      key: 'includeSubordinate',
      name: 'Zahrnout podřízené',
      cellExpr: (item) => (
        <Switch isChecked={flexGroupOrganizations.find(x => x.organizationUnit.id === item.id)?.includeSubordinate}
          onChange={(e) => updateOrganizationAsync?.(item.id, e.target.checked)} size='md' />
      ),
    })
  }

  columnsDefinitions.push({
    key: 'actions',
    name: '',
    cellExpr: (item) => (
      <CustomTooltip label="Odebrat">
        <Button
          size="sm"
          variant="ghost"
          color="red.800"
          ml="auto"
          onClick={() => removeOrganizationAsync(item.id)}
        >
          <Trash />
        </Button>
      </CustomTooltip>
    ),
  })

  return (
    <FormSection>
      <FormSectionHeader display='flex' flexDirection='column'>
        <FormSectionTitle>{title}</FormSectionTitle>

        <FormSectionDescription>
          {description}
        </FormSectionDescription>

        <AddFlexGroupOrganization
          onAddMember={addOrganizationAsync}
          alreadyAdded={organizations} />
      </FormSectionHeader>

      <FormSectionBody>

        <organizationListContext.Provider
          value={{
            loading: isLoading,
            totalCount: organizations?.length ?? 0,
            currentPage: filter.pageIndex,
            pageSize: organizations?.length ?? 0,
            filter,
            setCurrentPage: noop,
            setPageSize: noop,
            updateFilter: setFilter,
            reload: noop,
          }}
        >
          <OrganizationListFilter hideShowAll />
          <OrganizationsList
            organizations={filteredItems}
            columnsDefinition={columnsDefinitions}
            sx={{ width: 'full' }}
          />
        </organizationListContext.Provider>
      </FormSectionBody>
    </FormSection>
  )
}
